import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { HardDrive, Loader2 } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login, isLoading, testLogin } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await login(email, password);
      window.location.href = "/library";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-1 dark:bg-dark-surface-0 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-border-default bg-surface-0 p-8 shadow-md dark:border-dark-border-default dark:bg-dark-surface-1">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-accent text-white shadow-sm">
            <HardDrive size={20} strokeWidth={2.5} />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-primary dark:text-white">AttachDrive</h1>
        </div>

        <h2 className="text-lg font-semibold text-text-primary dark:text-dark-text-primary text-center">Welcome back</h2>
        <p className="mt-1 text-sm text-text-muted text-center">Sign in to your account</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border-strong bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100 dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-primary"
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-border-strong bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100 dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-primary"
              placeholder="••••••••"
              required
              minLength={8}
            />
          </div>

          {error && (
            <div className="rounded-lg bg-danger-50 border border-danger-100 px-3 py-2">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-50"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : null}
            {isLoading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-5 text-center text-sm">
          <span className="text-text-muted">Don&apos;t have an account? </span>
          <Link to="/signup" className="font-medium text-accent hover:text-accent-600 transition-colors">
            Sign up
          </Link>
        </div>

        {import.meta.env.DEV && (
          <button
            onClick={async () => {
              try {
                await testLogin();
                window.location.href = "/library";
              } catch (err) {
                setError(err instanceof Error ? err.message : "Test login failed");
              }
            }}
            className="mt-4 w-full rounded-lg border border-dashed border-border-strong py-2 text-xs text-text-muted transition-colors hover:bg-surface-2 dark:border-dark-border-strong dark:hover:bg-dark-surface-2"
          >
            Dev: Test Account Login
          </button>
        )}
      </div>
    </div>
  );
}
